/* ==========================================================================
   Rook UI — Flavor Carousel Controller (sem Swiper)
   Web Component: <rk-flavor-carousel>

   Variação do <rk-flavor-showcase> que NÃO usa Swiper. Implementa um carousel
   próprio no modelo "seletor circular de 3 slots", fiel ao Figma (sempre 3 latas
   visíveis: esquerda pequena · centro grande · direita pequena):

     - mantém um `index` (sabor ativo) e, a cada troca, classifica cada slide pelo
       seu offset circular em relação ao centro (is-center / is-prev / is-next /
       is-far-prev / is-far-next). O CSS posiciona/escala cada slot;
     - índice circular ⇒ loop infinito nos dois sentidos, sem clones;
     - lê data-bg/data-fg/data-flavor do slide ATIVO e aplica a cor de fundo
       "por sabor" na seção (--rk-flavor-bg/--rk-flavor-fg, com transição CSS);
     - sincroniza os dots (1 por sabor) e o card de produto (só o ativo aparece);
     - setas, clique numa lata lateral e arrastar/swipe navegam.

   Degrade suave: sem JS / antes de hidratar, as latas aparecem em linha estática
   e o card inicial (is-active do servidor) fica visível.

   Hydration tier: 'visible' — alinhado ao showcase original.
   ========================================================================== */

import { RkComponent } from '@rook/component';

const SWIPE_THRESHOLD = 40; // px de arrasto p/ trocar de sabor

class RkFlavorCarousel extends RkComponent {
  static hydration = 'visible';

  setup() {
    this.track = this.querySelector('.rk-flavor-native__track');
    this.slides = Array.from(this.querySelectorAll('.rk-flavor-showcase__slide'));
    this.cards = Array.from(this.querySelectorAll('.rk-flavor-showcase__card'));
    this.dots = Array.from(this.querySelectorAll('.rk-flavor-showcase__dot'));
    this.live = this.querySelector('[data-rk-flavor-live]');
    // Seção pai: onde aplicamos a cor de fundo por sabor.
    this.section = this.closest('.shopify-section');
    this.count = this.slides.length;

    if (this.count === 0) return;

    this.index = Number(this.dataset.initialIndex) || 0;
    if (this.index >= this.count || this.index < 0) this.index = 0;

    // Preenche os cards (preço + add-to-cart) a partir dos <template> dos slides.
    // O produto só resolve no contexto do bloco filho, então o card vem de lá.
    this.#fillCards();

    // A partir daqui o CSS passa a usar o layout absoluto de 3 slots.
    this.classList.add('is-ready');

    // ----- Clique: dots, setas e latas laterais -----
    this._onClick = (event) => {
      if (this._dragged) return; // ignora o "click fantasma" após um swipe

      const dot = event.target.closest('.rk-flavor-showcase__dot');
      if (dot && this.contains(dot)) {
        this.#goTo(Number(dot.dataset.dotIndex));
        return;
      }

      const arrow = event.target.closest('.rk-flavor-showcase__arrow');
      if (arrow && this.contains(arrow)) {
        this.#go(arrow.dataset.arrow === 'prev' ? -1 : 1);
        return;
      }

      // Clique numa lata lateral → centraliza aquele sabor.
      const slide = event.target.closest('.rk-flavor-showcase__slide');
      if (slide && this.contains(slide)) {
        const i = this.slides.indexOf(slide);
        if (i !== -1 && i !== this.index) this.#goTo(i);
      }
    };
    this.addEventListener('click', this._onClick);

    // ----- Arrastar/swipe (Pointer Events) -----
    this._dragged = false;
    this._pointerId = null;
    this._startX = 0;

    this._onPointerDown = (event) => {
      if (this._pointerId !== null) return;
      this._pointerId = event.pointerId;
      this._startX = event.clientX;
      this._dragged = false;
    };
    this._onPointerUp = (event) => {
      if (event.pointerId !== this._pointerId) return;
      const dx = event.clientX - this._startX;
      this._pointerId = null;
      if (Math.abs(dx) >= SWIPE_THRESHOLD) {
        this._dragged = true;
        this.#go(dx < 0 ? 1 : -1);
        // limpa o flag no próximo tick (depois do evento click sintético)
        setTimeout(() => { this._dragged = false; }, 0);
      }
    };
    this._onPointerCancel = (event) => {
      if (event.pointerId === this._pointerId) this._pointerId = null;
    };

    if (this.track) {
      this.track.addEventListener('pointerdown', this._onPointerDown);
      this.track.addEventListener('pointerup', this._onPointerUp);
      this.track.addEventListener('pointercancel', this._onPointerCancel);
    }

    // ----- Teclado: ←/→ navegam quando o carousel tem foco -----
    this._onKeydown = (event) => {
      if (event.key === 'ArrowLeft') { this.#go(-1); event.preventDefault(); }
      else if (event.key === 'ArrowRight') { this.#go(1); event.preventDefault(); }
    };
    this.addEventListener('keydown', this._onKeydown);

    this.#render();
  }

  // Preenche cada card-wrapper (vazio, vindo do bloco-pai) clonando o
  // <template> do slide correspondente (casado por data-flavor-key). O card de
  // produto é renderizado no contexto do bloco filho — único lugar onde a setting
  // de tipo recurso `product` resolve. Os custom elements nativos
  // (product-form-component etc.) hidratam ao serem inseridos no DOM.
  #fillCards() {
    this.cards.forEach((card) => {
      if (card.childElementCount > 0) return; // já preenchido
      const key = card.dataset.flavorKey;
      if (!key) return;
      // O child.id (wrapper, no pai) e o block.id (slide, no filho) referem o
      // mesmo bloco, mas um lado pode vir com sufixo de instância (ex.: o wrapper
      // `...r8DYca-1` vs. o slide `...r8DYca`). Casamos quando um é prefixo do
      // outro na fronteira `-` — tolera a direção do sufixo e clones de loop.
      const slide = this.slides.find((s) => {
        const sk = s.dataset.flavorKey;
        return sk && (sk === key || sk.indexOf(key + '-') === 0 || key.indexOf(sk + '-') === 0);
      });
      const tpl = slide && slide.querySelector('.rk-flavor-showcase__card-template');
      if (tpl && tpl.content) card.appendChild(tpl.content.cloneNode(true));
    });
  }

  // Avança/retrocede de forma circular (loop infinito).
  #go(delta) {
    this.index = (this.index + delta + this.count) % this.count;
    this.#render();
  }

  #goTo(target) {
    this.index = ((target % this.count) + this.count) % this.count;
    this.#render();
  }

  // Offset circular do slide `i` em relação ao centro, normalizado em
  // [-floor(n/2), +ceil(n/2)] → define qual slot o slide ocupa.
  #offset(i) {
    const n = this.count;
    let d = i - this.index;
    if (d > n / 2) d -= n;
    else if (d < -n / 2) d += n;
    return d;
  }

  #render() {
    this.slides.forEach((slide, i) => {
      const d = this.#offset(i);
      slide.classList.remove('is-center', 'is-prev', 'is-next', 'is-far-prev', 'is-far-next');
      if (d === 0) slide.classList.add('is-center');
      else if (d === -1) slide.classList.add('is-prev');
      else if (d === 1) slide.classList.add('is-next');
      else if (d < -1) slide.classList.add('is-far-prev');
      else slide.classList.add('is-far-next');
      slide.setAttribute('aria-hidden', d === 0 ? 'false' : 'true');
    });

    const active = this.slides[this.index];

    // Cor de fundo + texto a partir do slide ativo.
    if (active && this.section) {
      if (active.dataset.bg) this.section.style.setProperty('--rk-flavor-bg', active.dataset.bg);
      if (active.dataset.fg) this.section.style.setProperty('--rk-flavor-fg', active.dataset.fg);
    }

    // Anúncio do sabor para leitores de tela.
    if (active && this.live && active.dataset.flavor) {
      this.live.textContent = active.dataset.flavor;
    }

    // Card ativo: só o card do sabor centralizado aparece (largura cheia, abaixo).
    this.cards.forEach((card, i) => {
      const on = i === this.index;
      card.classList.toggle('is-active', on);
      if (on) card.removeAttribute('aria-hidden');
      else card.setAttribute('aria-hidden', 'true');
    });

    // Dot ativo.
    this.dots.forEach((dot, i) => {
      const on = i === this.index;
      dot.classList.toggle('is-active', on);
      dot.setAttribute('aria-selected', on ? 'true' : 'false');
    });
  }

  teardown() {
    if (this._onClick) this.removeEventListener('click', this._onClick);
    if (this._onKeydown) this.removeEventListener('keydown', this._onKeydown);
    if (this.track) {
      this.track.removeEventListener('pointerdown', this._onPointerDown);
      this.track.removeEventListener('pointerup', this._onPointerUp);
      this.track.removeEventListener('pointercancel', this._onPointerCancel);
    }
    this.classList.remove('is-ready');
  }
}

if (!customElements.get('rk-flavor-carousel')) {
  customElements.define('rk-flavor-carousel', RkFlavorCarousel);
}
